// Ancestry's default is ON, which is why this gate exists: WheelModal is one component with
// three mount points and one of them is the patient dashboard (patient/Dashboard.tsx:1257), and
// it takes no prop saying which surface it is on. Ancestry has to be withheld, not given.
// The gate is configuration so enabling patients later is one value, not a rewrite.
// NOT an authorisation boundary — the backend decides who may read whose ancestry.
// Position document: https://wiki.mygene.com.au/Information-Technology/Xoe/compliance/ancestry-patient-visibility-position (ClickUp 86d43jb70).

export type AncestrySurface = "practitioner-results" | "lab-sample" | "patient-dashboard";

// The app's existing vocabulary for `user_type` off the `user` cookie (Sidebar.tsx:254).
export type AncestryViewerType = "clinical" | "patient";

export interface AncestryVisibilityConfig {
	patientDashboardEnabled: boolean;
}

// Not read from runtimeEnv: a default that varies by environment is a default that can be wrong.
export const ANCESTRY_VISIBILITY_CONFIG: AncestryVisibilityConfig = {
	// Default-on for every patient, no practitioner release: Alexander, 2026-09-29, ClickUp 14ygtkja108 S11.
	patientDashboardEnabled: true,
};

export type AncestryHiddenReason =
	| "surface_not_recognised"
	| "patient_visibility_not_enabled"
	| "viewer_not_permitted_on_surface";

export type AncestryVisibility =
	| { reachable: true; surface: AncestrySurface }
	| { reachable: false; reason: AncestryHiddenReason };

export interface AncestryViewerContext {
	// Widened to string: the caller reads it from props or routing and may pass anything.
	surface?: AncestrySurface | string | null;
	userType?: AncestryViewerType | string | null;
}

const KNOWN_SURFACES: readonly AncestrySurface[] = ["practitioner-results", "lab-sample", "patient-dashboard"];

const isKnownSurface = (value: unknown): value is AncestrySurface =>
	typeof value === "string" && (KNOWN_SURFACES as readonly string[]).includes(value);

export const resolveAncestryVisibility = (
	{ surface, userType }: AncestryViewerContext,
	config: AncestryVisibilityConfig = ANCESTRY_VISIBILITY_CONFIG,
): AncestryVisibility => {
	// `WheelModal` passes no surface today, so "I forgot to pass it" must fail closed.
	if (!isKnownSurface(surface)) {
		return { reachable: false, reason: "surface_not_recognised" };
	}

	if (surface === "patient-dashboard") {
		if (config.patientDashboardEnabled !== true) {
			return { reachable: false, reason: "patient_visibility_not_enabled" };
		}
		return { reachable: true, surface };
	}

	// A patient reaching a clinical route by any means does not inherit clinical visibility, and an
	// unreadable `user_type` degrades to hidden rather than to allowed.
	if (userType !== "clinical") {
		return { reachable: false, reason: "viewer_not_permitted_on_surface" };
	}

	return { reachable: true, surface };
};

export const isAncestryReachable = (
	context: AncestryViewerContext,
	config: AncestryVisibilityConfig = ANCESTRY_VISIBILITY_CONFIG,
): boolean => resolveAncestryVisibility(context, config).reachable;
